import React, { FC, useContext, useMemo } from 'react';

import { useFormikContext } from 'formik';

import BasicButton from 'components/buttons/basicButton';
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';

import { EditorContext } from '../../../../../context/EditorContext';

import useVariantsByMessageContentIdQuery from 'hooks/variants/useVariantsByMessageContentIdQuery';

import { FormulaTestMessageFormValues } from 'models/formula.model';
import { VariantWithLabel } from 'models/variant.model';

import { pickRandomVariants } from './helper';

const TestFormulaRandomizeButton: FC = () => {
    const { editorDetails, setEditorDetails } = useContext(EditorContext);
    const { formula } = editorDetails;

    const isLabeledMatchingEnabled = useMemo<boolean>(
        () => formula?.is_labeled_matching_enabled ?? false,
        [formula],
    );

    const { setFieldValue } = useFormikContext<FormulaTestMessageFormValues>();

    const { data: messageContentVariants = [] } =
        useVariantsByMessageContentIdQuery(+editorDetails?.formula?.id!);

    const groupedByComponent = useMemo<
        Record<string, VariantWithLabel[]>
    >(() => {
        return messageContentVariants.reduce<
            Record<string, VariantWithLabel[]>
        >((acc, v) => {
            if (!acc[v.component_id]) acc[v.component_id] = [];
            acc[v.component_id].push(v);
            return acc;
        }, {});
    }, [messageContentVariants]);

    const componentsByType = useMemo<Record<string, Set<string>>>(() => {
        return messageContentVariants.reduce<Record<string, Set<string>>>(
            (acc, v) => {
                if (!acc[v.component_type_id])
                    acc[v.component_type_id] = new Set();
                acc[v.component_type_id].add(v.component_id);
                return acc;
            },
            {},
        );
    }, [messageContentVariants]);

    const onselectRandomFilters = () => {
        setEditorDetails({
            ...editorDetails,
            selectRandomFilters: true,
        });
        const pickedVariants = pickRandomVariants(
            groupedByComponent,
            componentsByType,
            isLabeledMatchingEnabled,
        );
        pickedVariants?.forEach((variant) => {
            setFieldValue(
                `filters.alternates.${variant.component_id}`,
                variant.variant.id,
            );
        });
        const componentAttachedLabelMap = pickedVariants?.reduce(
            (acc, component) => {
                return {
                    ...acc,
                    [component.component_id]: {
                        componentTypeId: component.variant.component_type_id,
                        labelId: component.variant.label?.id,
                    },
                };
            },
            {},
        );
        setFieldValue('componentAttachedLabel', componentAttachedLabelMap);
    };

    return (
        <StyledBasicButton
            name={'Randomize'}
            onClick={onselectRandomFilters}
            type={'button'}
        />
    );
};

export default TestFormulaRandomizeButton;

const StyledBasicButton = styled(BasicButton)({
    backgroundColor: 'transparent',
    color: aampeTheme.colors.solidA01,
});
